import { ServiceTile, Medallion } from '../awards/AwardBadge';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { SERVICE_MILESTONES } from '../awards/serviceAwards';
import { parseHireDate } from '../../utils/hireDate';

/**
 * Inspect view for a badge the person already holds.
 *
 * Clicking a trophy used to reuse BadgeCelebration — the earn-moment — so
 * inspecting a badge you had owned for years replayed a full confetti
 * celebration. Worse, ProfileModal called it as
 * `triggerCelebration({tier: badge.tier || 1, …})` with no `badgeType`, and
 * BadgeCelebration defaults to 'tenure': achievements carry no `tier`, so
 * `|| 1` resolved to tier 1 and clicking "Picture Perfect" played the
 * ⭐ Rising Star *tenure* celebration.
 *
 * Earn-moment and inspect-moment are now separate surfaces. This one is quiet:
 * what the badge is, what earned it, and when.
 */

function earnedDateLabel(badge, hireDate) {
  if (badge.category === 'tenure') {
    if (!hireDate) return null;
    const years = SERVICE_MILESTONES[badge.tier] ?? 0;
    const earned = parseHireDate(hireDate);
    if (!earned) return null;
    earned.setFullYear(earned.getFullYear() + years);
    return earned.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  }
  if (!badge.earnedAt) return null;
  return new Date(badge.earnedAt).toLocaleDateString(undefined, {
    month: 'long', day: 'numeric', year: 'numeric',
  });
}

function nextMilestone(badge, hireDate) {
  if (badge.category !== 'tenure' || !hireDate) return null;
  const years = SERVICE_MILESTONES[badge.tier] ?? 0;
  const next = SERVICE_MILESTONES.find((m) => m > years);
  if (!next) return null;
  const at = parseHireDate(hireDate);
  if (!at) return null;
  at.setFullYear(at.getFullYear() + next);
  return { years: next, when: at.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }) };
}

export function BadgeDetail({ badge, employeeName, hireDate, onClose }) {
  if (!badge) return null;

  const isTenure = badge.category === 'tenure';
  const earned = earnedDateLabel(badge, hireDate);
  const next = nextMilestone(badge, hireDate);

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.97, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.97, y: 8 }}
          transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label={`${badge.name} badge details`}
          className="w-full max-w-xs rounded-card bg-surface border border-line shadow-e3 overflow-hidden"
        >
          <div className="flex items-start gap-3 p-4">
            {/* Same award object as the trophy case and the earn moment, at 56px.
                Inspecting a badge should show the badge, not a stand-in. */}
            {isTenure
              ? <ServiceTile years={badge.years ?? 0} size={56} />
              : <Medallion size={56} metal="brand">{badge.icon}</Medallion>}
            <div className="min-w-0 flex-1">
              <p className="text-[15px] font-bold text-ink leading-tight">{badge.name}</p>
              <p className="text-[11.5px] text-ink-3 mt-1 leading-snug">{badge.description}</p>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="flex-none p-1 rounded-full text-ink-4 hover:text-ink-2 transition-colors"
            >
              <X size={14} />
            </button>
          </div>

          <dl className="px-4 pb-4 pt-1 space-y-2">
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-[9.5px] font-bold uppercase tracking-[0.12em] text-ink-4">Type</dt>
              <dd className="text-[11.5px] text-ink-2">
                {isTenure ? 'Service milestone — automatic' : 'Achievement — earned'}
              </dd>
            </div>
            {earned && (
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-[9.5px] font-bold uppercase tracking-[0.12em] text-ink-4">Earned</dt>
                <dd className="text-[11.5px] text-ink-2">{earned}</dd>
              </div>
            )}
            {next && (
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-[9.5px] font-bold uppercase tracking-[0.12em] text-ink-4">Next</dt>
                <dd className="text-[11.5px] text-ink-2">
                  {next.years} years · {next.when}
                </dd>
              </div>
            )}
          </dl>

          {employeeName && (
            <p className="px-4 py-2.5 text-[10.5px] text-ink-4 border-t border-line bg-surface-2">
              Held by {employeeName}
            </p>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
}
